/**
 * The call itself: what was said and what the agent did, in ONE column.
 *
 * THIS USED TO BE TWO TABS, and the split was hiding the only relationship on the page
 * worth seeing. "The conversation" showed eight turns; "What the agent did" showed five
 * tool calls with timestamps on them; and the question anybody actually has - *what did
 * the caller say that made it quote twenty-three thousand dollars* - could only be
 * answered by holding one tab in your head while reading the other. Both halves carry a
 * clock. Interleaved, the answer is the line above the action.
 *
 * TURNS CARRY THE CLOCK FORWARD. `atMs` is per-turn and nullable - a host-supplied
 * transcript arrives properly aggregated and with no timings at all - so a turn with no
 * time of its own inherits the last one that had one. That places it after everything
 * before it, which is the true statement, rather than inventing a second on the wire.
 * When NO turn in the call has a time, nothing can be interleaved honestly: the actions
 * are listed after the transcript, under a line saying why.
 *
 * TIES GO TO THE TURN. A tool fires because of what somebody just said, so at equal
 * milliseconds the words come first; the action reads as a consequence, which is what it
 * is.
 *
 * AN ACTION IS SHUT UNTIL YOU OPEN IT, which is what lets both jobs happen in one
 * column. Reading a transcript as prose - checking a quote, or what was promised about
 * installation - is a different job from auditing the agent, and eight blocks of
 * arguments in the middle of the words serve neither. Collapsed, an action is one line
 * that says what the agent did and what came back; opened, it is every argument and
 * every field of the result, untruncated. Nothing is hidden and nothing is in the way.
 */

import { Fragment, useMemo } from "react";
import type { TimelineEvent, Turn } from "../api";
import { humanise, label, offset, round, spaced, title, unit } from "../format";
import { Panel, cx } from "../ui";
import { Quote, Route } from "./Icons";
import { Empty } from "./States";

/**
 * What the SHUT LINE is allowed to carry. Two pairs and sixty characters is about the
 * width of the turn above it; past that the action stops being a line and starts being a
 * paragraph, which is the thing the collapse exists to prevent. Everything else is one
 * click away, in full.
 *
 * THIS IS A DRAWING RULE AND NOT A DATA ONE, which it was not. `entries()` used to cut
 * every value to sixty characters before anything rendered, so the OPEN card - the one
 * place that promises "every argument and every field, untruncated" - showed the same
 * `The shop's at Rose Bay, inside Woollahra Sailing Club - ope…` as the line you clicked
 * to get away from it. A tool's instruction paragraph and a JSON blob are exactly the
 * values worth opening a card for, and they were the values it could never show.
 */
const GLANCE_PAIRS = 2;
const MAX_VALUE_CHARS = 60;

/**
 * Past this, an open card's value gets a lid of its own rather than the whole card
 * growing a wall of text.
 *
 * IT IS SET ABOVE WHAT FOUR CLAMPED LINES HOLD, not at it. The lid is CSS - four lines,
 * then `overflow: hidden` - and CSS cannot tell the component whether it actually cut
 * anything, so the number has to. The card is `max-w-[78ch]`, its value column is about
 * 65 characters at the widest it is ever drawn, and four of those lines is roughly 260;
 * anything past 280 is therefore hiding something in every layout this card has. Under
 * it a value simply prints, which is the right outcome for a two-line note and avoids
 * the worst version of this control: a "Show all" that reveals nothing.
 */
const LONG_VALUE = 280;

type Row =
  | { kind: "turn"; key: string; at: number; index: number; turn: Turn }
  | { kind: "event"; key: string; at: number; event: TimelineEvent };

export function Conversation({
  turns,
  events,
  truncated,
  omitted,
  bare,
}: {
  turns: Turn[];
  events: TimelineEvent[];
  truncated?: boolean;
  omitted?: number;
  /**
   * Drop the card. In the call dialog the transcript IS the left pane - it has the pane's
   * own scroll and the pane's own heading - and a card inside it would be a border drawn
   * one pixel inside another border.
   */
  bare?: boolean;
}) {
  const { rows, placed } = useMemo(() => merge(turns, events), [turns, events]);
  const Wrapper = bare ? Fragment : Panel;

  return (
    <Wrapper {...(bare ? {} : { title: "The conversation", icon: <Quote /> })}>
      {!turns.length && (
        <Empty
          compact
          icon={Quote}
          title="No transcript for this call"
          /* "The call was answered" is the load-bearing clause. Without it a blank panel
             headed "The conversation" reads as something having gone wrong with the
             CALL, which is the more alarming of the two things it could mean and the
             wrong one. */
          body="The call was answered, but its transcript never reached us."
          hint="Tell us and we'll turn capture on for this agent — every call after that will have one."
        />
      )}

      <div className="grid gap-0.5">
        {rows.map((row, index) => (
          <Fragment key={row.key}>
            {/* The one place the merge admits it could not merge. */}
            {!placed && turns.length > 0 && row.kind === "event" && rows[index - 1]?.kind !== "event" && (
              <p className="mt-2 mb-1 text-sm text-ink-faint">
                This call's transcript arrived without timings, so the agent's actions are listed
                after it rather than beside the turns that caused them.
              </p>
            )}
            {row.kind === "turn" ? (
              <TurnRow turn={row.turn} index={row.index} />
            ) : (
              <ActionRow event={row.event} />
            )}
          </Fragment>
        ))}
      </div>

      {turns.length > 0 && events.length === 0 && (
        <p className="mt-3.5 text-sm text-ink-faint">
          {/* "called no tools" on a page about phone calls parses as an unanswered call
              for about half a second, every time. "used" costs nothing and never does. */}
          The agent used no tools on this call — it answered from its brief alone.
        </p>
      )}

      {truncated && (
        // Said plainly rather than hidden. A dashboard that quietly shows most of a
        // conversation is one somebody will eventually catch it doing, and then stop
        // trusting the parts that were complete.
        <p className="mt-3.5 rounded-sm border border-warm-line bg-warm-soft px-3 py-2.5 text-sm text-warm">
          The middle of this call is not stored — {omitted ?? "some"} turns were dropped to keep
          the record within its size limit. The opening and the ending are complete.
        </p>
      )}
    </Wrapper>
  );
}

/**
 * A turn is a row, not a bubble.
 *
 * Chat bubbles put the two speakers on opposite sides of the panel, which halves the
 * measure for both and turns a 300-word agent answer into a ragged column. A left rail
 * for the speaker and a full-width paragraph reads like a transcript, which is what it
 * is.
 *
 * THE OFFSET COLUMN exists because a transcript without one cannot be checked against
 * anything. "The agent got the price wrong" is a complaint you can only settle if you can
 * find the moment. Where a turn carries no clock the column says which turn it is
 * instead. IT NEVER GUESSES A TIME: a fabricated timestamp on evidence is worse than
 * none.
 *
 * INTERRUPTIONS ARE MARKED because an agent that gets cut off mid-sentence twenty times
 * in a call has a turn-detection problem, and that is invisible in a transcript that
 * renders every turn as if it finished.
 */
function TurnRow({ turn, index }: { turn: Turn; index: number }) {
  return (
    <div
      className={cx(
        "grid grid-cols-[96px_minmax(0,1fr)] gap-3.5 rounded-sm px-2.5 py-2 hover:bg-sunken",
        // A CONTAINER QUERY, NOT A WINDOW ONE. This pane is ~864px inside a 1440px dialog
        // on a 1600px window, so `max-[640px]` was asking the wrong box and got the wrong
        // answer in both directions. Same fix as `Qualification.tsx` and `Fields.tsx`.
        "@max-[640px]:grid-cols-[minmax(0,1fr)] @max-[640px]:gap-1",
      )}
    >
      {/*
        THE SPEAKER OVER THE CLOCK, not beside it. Set inline, the two ran together as one
        grey-and-coloured token - `AGENT 0:12` - and the eye had to parse a label out of a
        timestamp on every turn. Stacked, the column scans as a list of who spoke, with the
        time available when somebody is actually looking for a moment. Narrow, they go back
        to one line, because a two-line gutter above a one-line turn is mostly white.
      */}
      <div
        className={cx(
          "grid gap-px pt-[3px] text-2xs font-semibold tracking-[0.05em] uppercase",
          "@max-[640px]:flex @max-[640px]:items-baseline @max-[640px]:gap-1.5 @max-[640px]:pt-0",
          turn.role === "caller" ? "text-band-won" : "text-band-handled",
        )}
      >
        <span>{turn.role === "caller" ? "Caller" : "Agent"}</span>
        <span className="nums font-mono font-normal text-ink-faint">
          {offset(turn.atMs) || `#${index + 1}`}
        </span>
      </div>
      <div className="max-w-[78ch] text-base leading-relaxed">
        {turn.text}
        {turn.interrupted && (
          <span className="ml-2 inline-flex items-center rounded-full border border-warm-line bg-warm-soft px-1.5 align-[1px] text-2xs font-semibold text-warm">
            cut off
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * What the agent did, as a card between two turns - shut, until somebody wants it.
 *
 * DRAWN AS A STRIP RATHER THAN AS A THIRD SPEAKER. It sits in the transcript's second
 * column, tinted and one step smaller, so a reader scanning the words sees it as
 * machinery beside the conversation rather than as somebody talking. The rail-and-node
 * drawing this replaced was a picture of a SEQUENCE - which is exactly the job the
 * transcript's own order now does, at no cost.
 *
 * THE SHUT LINE IS THE HEADLINE, NOT A LABEL. `quote indicative price` on its own makes
 * a reader open every card to find the one that matters, so the line carries the RESULT
 * where there is one - *quoted indicative price → total from 23,460* - and the arguments
 * only where there is no result to show. That is the sentence somebody actually wants:
 * the tool fired, and this came back.
 *
 * A `details` ELEMENT, NOT A PIECE OF STATE. It opens without React, it survives Cmd-F -
 * the browser opens a closed `details` to reveal a match inside it - and it prints open.
 * A `useState` toggle does none of those three.
 *
 * The rendering rule for arguments is unchanged: key/value pairs, never pretty-printed
 * JSON. An owner does not read JSON, and a tool call whose interesting content is
 * `{"verdict": "suitable"}` should say `verdict suitable`.
 */
function ActionRow({ event }: { event: TimelineEvent }) {
  const args = entries(event.args);
  const result = entries(event.result);
  // The result is the interesting half where there is one: a tool that came back with
  // `total from 23,460` is a fact, and its arguments are how it got there.
  const headline = result.length ? result : args;
  const glance = headline.slice(0, GLANCE_PAIRS);
  // Nothing to open when the shut line already showed everything there is - and a value
  // the line had to CLIP counts as something it did not show. Without that second test a
  // single-field tool whose one field is a paragraph drew no chevron at all, so the only
  // copy of that paragraph on the screen was the clipped one.
  const more =
    args.length + result.length > glance.length ||
    glance.some(([, value]) => value.length > MAX_VALUE_CHARS);

  const tone =
    event.kind === "error"
      ? { box: "border-alert-line bg-alert-soft", mark: "text-alert", name: "text-alert" }
      : event.kind === "handoff"
        ? { box: "border-warm-line bg-warm-soft", mark: "text-warm", name: "text-ink" }
        : { box: "border-line-soft bg-sunken", mark: "text-ink-ghost", name: "text-ink" };

  return (
    <div
      className={cx(
        "grid grid-cols-[96px_minmax(0,1fr)] gap-3.5 px-2.5 py-1",
        "@max-[640px]:grid-cols-[minmax(0,1fr)] @max-[640px]:gap-1",
      )}
    >
      <div className="nums pt-[9px] font-mono text-2xs text-ink-faint @max-[640px]:pt-0">
        {offset(event.atMs)}
      </div>

      {/* A card with nothing left to show is not a `details`. A disclosure that opens on
          nothing is a broken control, and there is no honest chevron to draw on it. */}
      {more ? (
        <details
          className={cx(
            "action max-w-[78ch] rounded-sm border text-sm",
            // The shut card is quiet enough to read past; the open one is a thing you are
            // looking at, and says so with one step more contrast.
            "hover:border-line open:border-line",
            tone.box,
          )}
        >
          <summary className="flex cursor-pointer items-center gap-2 px-3 py-1.5">
            <Line event={event} tone={tone} glance={headline} />
            <Chevron />
          </summary>

          {/* ONE GRID FOR BOTH BAGS. Two grids side by side size their label columns
              independently, so "verdict" and "current engine hp" put their values in two
              different places and the card reads as two unrelated tables. */}
          <dl className="m-0 grid grid-cols-[minmax(96px,max-content)_minmax(0,1fr)] gap-x-4 gap-y-[5px] border-t border-line px-3 pt-2.5 pb-3">
            {args.length > 0 && <Bag label="Asked for" rows={args} />}
            {result.length > 0 && <Bag label="Got back" rows={result} />}
          </dl>
        </details>
      ) : (
        <div className={cx("flex max-w-[78ch] items-center gap-2 rounded-sm border px-3 py-1.5 text-sm", tone.box)}>
          <Line event={event} tone={tone} glance={headline} />
        </div>
      )}
    </div>
  );
}

/**
 * The shut line, which is the same line whether or not the card opens.
 *
 * THE LABEL IS QUIETER THAN THE VALUE, and that is the whole readability of this line.
 * Set in one flat grey it read as a run of words - *found yes lastVisit 2026-02-14* -
 * and the eye had nothing to land on. "Last visit" is scaffolding and `2026-02-14` is
 * the fact, so they are two different greys and the fact is the darker one.
 */
function Line({
  event,
  tone,
  glance,
}: {
  event: TimelineEvent;
  tone: { mark: string; name: string };
  glance: Array<[string, string]>;
}) {
  const shown = glance.slice(0, GLANCE_PAIRS);
  const rest = glance.length - shown.length;
  // Clipped HERE, where the one-line rule lives, rather than in `entries` where it would
  // follow the value into the open card.
  const clip = (value: string) =>
    value.length > MAX_VALUE_CHARS ? `${value.slice(0, MAX_VALUE_CHARS - 1)}…` : value;

  return (
    <>
      {/* THE GLYPH IN A TINTED SQUARE, which is the one thing the prototype's version of
          this line had that the bare icon did not: a fixed 22px object at the head of every
          action, so the column of them reads as a rail down the transcript rather than as
          eight differently-sized runs of text starting at eight different places. */}
      <span
        aria-hidden="true"
        className={cx(
          "grid size-[22px] flex-none place-items-center rounded-xs bg-paper",
          tone.mark,
        )}
      >
        <Route size={14} />
      </span>
      <span className={cx("flex-none font-[550]", tone.name)}>{title(event.name)}</span>
      {shown.length > 0 && (
        // ONE LINE, ALWAYS. A headline that wraps to three lines is the block this card
        // exists to fold away, so it truncates and the full text is inside it.
        <span className="min-w-0 flex-1 truncate">
          {shown.map(([key, value], index) => (
            <Fragment key={key}>
              {index > 0 && <span className="text-ink-faint"> · </span>}
              <span className="text-ink-soft">{key} </span>
              <span className="text-ink">{clip(value)}</span>
            </Fragment>
          ))}
          {rest > 0 && <span className="text-ink-soft"> · +{rest} more</span>}
        </span>
      )}
    </>
  );
}

/**
 * The open card's contents: every field, in full, and never JSON.
 *
 * Two bags rather than one list, because "what the agent asked for" and "what came back"
 * are different kinds of fact and reading them as one run of pairs is how you end up
 * believing the agent supplied a price it was told.
 */
function Bag({ label, rows }: { label: string; rows: Array<[string, string]> }) {
  return (
    <>
      <div className="col-span-2 mt-2 text-2xs font-semibold tracking-[0.055em] text-ink-soft uppercase first:mt-0">
        {label}
      </div>
      {rows.map(([key, value]) => (
        <Fragment key={key}>
          <dt className="text-ink-soft">{key}</dt>
          <dd className="m-0 font-medium text-ink [overflow-wrap:anywhere]">
            <Long text={value} />
          </dd>
        </Fragment>
      ))}
    </>
  );
}

/**
 * A value long enough to need a lid: four lines, then the rest on request.
 *
 * FOLDED, NOT CUT. An ellipsis says "there is more and you cannot have it"; this says
 * "there is more" and gives it to you, which is the only honest thing to do inside a card
 * whose entire promise is that it holds the full record of what the agent did.
 *
 * ONE COPY OF THE TEXT, INSIDE THE `summary`. The obvious build - a clipped preview in
 * the summary and the full text in the body - puts the same paragraph in the DOM twice,
 * so a reader searching the page finds the half of it that is hidden. Here the text is
 * written once and the CLAMP is what the disclosure removes, so Cmd-F has one thing to
 * find and printing gets the whole value. Same `details` reasoning as the card around it.
 */
function Long({ text }: { text: string }) {
  if (text.length <= LONG_VALUE) return <>{text}</>;
  return (
    <details className="longer">
      <summary className="cursor-pointer">
        <span className="longer-text">{text}</span>
        {/* The word is set by the stylesheet, because it is the one thing here that has to
            know whether its ANCESTOR is open. `text-accent` marks it as the control. */}
        <span className="longer-more mt-0.5 block text-2xs font-semibold text-accent" />
      </summary>
    </details>
  );
}

/** The affordance, and the only thing on the card that moves. Rotated in `styles.css`. */
function Chevron() {
  return (
    <svg
      aria-hidden="true"
      className="action-chevron ml-auto flex-none text-ink-ghost"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/**
 * The two halves of the call in one order.
 *
 * `placed` is false when not one turn carried a time, which is the case the caller has
 * to say something about rather than paper over.
 */
function merge(turns: Turn[], events: TimelineEvent[]): { rows: Row[]; placed: boolean } {
  let carried = -1;
  const rows: Row[] = turns.map((turn, index) => {
    if (turn.atMs !== null && turn.atMs !== undefined) carried = turn.atMs;
    return { kind: "turn", key: `t${index}`, at: carried, index, turn };
  });
  const placed = carried >= 0;

  events.forEach((event, index) => {
    rows.push({ kind: "event", key: `e${index}-${event.atMs}-${event.name}`, at: event.atMs, event });
  });

  // Stable, so turns keep their own order (their keys never decrease) and an action that
  // shares a millisecond with the turn that caused it lands underneath it.
  return {
    rows: rows.sort((a, b) => a.at - b.at || rank(a) - rank(b)),
    placed,
  };
}

function rank(row: Row): number {
  return row.kind === "turn" ? 0 : 1;
}

/**
 * A bag of arguments as label/value rows, dropping only what is genuinely empty.
 *
 * THE KEYS GO THROUGH `label`, the same function the fields panel uses, because they are
 * the same kind of thing: somebody else's JSON key, shown to somebody who has never seen
 * JSON. Raw, this panel printed `lastVisit 2026-02-14` - camelCase in front of a dentist.
 * `label` also carries the unit, so `motorKw: 7` reads "Motor · 7 kW" rather than
 * repeating the unit in the label and dropping it from the number.
 */
function entries(bag: Record<string, unknown> | null): Array<[string, string]> {
  if (!bag) return [];
  return Object.entries(bag)
    .filter(([, value]) => value !== null && value !== "" && value !== undefined)
    .map(([key, value]) => [label(key), scalar(value, key)] as [string, string]);
}

function scalar(value: unknown, key?: string): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    const suffix = key ? unit(key)?.suffix : undefined;
    return `${round(value)}${suffix ? spaced(suffix) : ""}`;
  }
  if (typeof value === "string") return humanise(value);
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? "" : "s"}`;
  if (value && typeof value === "object") return JSON.stringify(value);
  return String(value);
}
